
import React from "react";
import { i18nManager } from "../../../../i18n";
import styles from "../assets/style.module.css";
import * as ReactRouterDom from "react-router-dom";
import { ApiResponseData, BaseService, IHostManager, IHostWebsite, ITeamMember, OmneHostService, SanitizedResponse } from "../../../../services";
import { Utils } from "../../../../utils";
import { BoolHelper, Classname } from "@rivtn/noseur";

interface StaffModalProps {
    onClose: () => void;
    member: IHostManager;
}

export function StaffModal({ member: m, onClose }: StaffModalProps) {
    return (
        <div className={styles["modal-overlay"]} onClick={e => e.target === e.currentTarget && onClose()}>
            <div className={styles["modal"]}>
                <button className={styles["modal-close"]} onClick={onClose}>✕</button>

                <div className={styles["staff-modal-banner"]}>
                    <img className={styles["staff-modal-banner-img"]} src={m.half_body_picture_url} alt="" />
                    <div className={styles["staff-modal-banner-overlay"]} />
                    <img className={styles["staff-modal-avatar"]} src={m.passport_picture_url} alt={m.display_name} />
                </div>

                <div className={styles["staff-modal-body"]}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.25rem", flexWrap: "wrap" }}>
                        <div className={styles["staff-modal-name"]}>{m.display_name}</div>
                        <span className={styles["staff-role-badge"]}>{m.display_role ?? m.role}</span>
                        <span className={styles["status-active"]} style={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "1px" }}>● Active</span>
                    </div>

                    <div style={{ color: "var(--muted)", fontSize: "0.85rem", marginBottom: "1.25rem", lineHeight: 1.7 }}>{m.profile_details}</div>

                    <div className={styles["staff-info-grid"]}>
                        {(!!m.settings.country) && (<div className={styles["staff-info-box"]}>
                            <div className={styles["staff-info-lbl"]}>{"Country"}</div>
                            <div className={styles["staff-info-val"]} style={{ color: "var(--text)" }}>{m.settings.country}</div>
                        </div>)}
                        {(!!m.settings.state_region) && (<div className={styles["staff-info-box"]}>
                            <div className={styles["staff-info-lbl"]}>{"State / Region"}</div>
                            <div className={styles["staff-info-val"]} style={{ color: "var(--text)" }}>{m.settings.state_region}</div>
                        </div>)}
                        <div className={styles["staff-info-box"]}>
                            <div className={styles["staff-info-lbl"]}>{"Role"}</div>
                            <div className={styles["staff-info-val"]} style={{ color: "var(--text)" }}>{m.display_role ?? m.role}</div>
                        </div>
                        {(!!m.settings.show_link_to_profile) && (<div className={styles["staff-info-box"]}>
                            <div className={styles["staff-info-lbl"]}>{"Profile Link"}</div>
                            <ReactRouterDom.NavLink to={`${window.location.origin}/me/${m.member_id}`} target="_blank" style={{ color: "var(--gold)", textDecoration: "none" }} className={styles["staff-info-val"]}>
                                View Profile
                            </ReactRouterDom.NavLink>
                        </div>)}
                    </div>

                    {Object.keys(m.social_accounts).length > 0 && (<>
                        <div style={{ fontSize: "0.7rem", color: "var(--muted)", letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "0.75rem" }}>Social Networks</div>
                        <div className={styles["about-social-row"]}>
                            {Object.entries(m.social_accounts).map(([social, username]) => (<ReactRouterDom.NavLink target="_blank" key={social} to={Utils.getSocialLink(social, username)} className={styles["social-pill"]} style={{ textDecoration: "none" }}>
                                <i className={Classname.build(styles["icon"], Utils.getSocialIcon(social))} /> {username}
                            </ReactRouterDom.NavLink>))}
                        </div>
                    </>)}

                    {(m.settings.show_link_to_profile) && (<div style={{ marginTop: "2.5rem" }}>
                        <ReactRouterDom.NavLink to={`${window.location.origin}/me/${m.member_id}`} target="_blank" className={styles["btn-ghost"]} style={{ fontSize: "0.82rem", padding: "0.5rem 1.2rem", textDecoration: "none" }}>
                            View Full Profile →
                        </ReactRouterDom.NavLink>
                    </div>)}
                </div>
            </div>
        </div>
    );
}

interface AboutProps {
    website: IHostWebsite;
}

function About(props: AboutProps) {

    const { website } = props;
    const nameSplit = website.host.name?.split(/\s+/);
    const [nameFirstWord, ...nameRemain] = nameSplit;
    const omnehostService = OmneHostService.getInstance();
    const [managers, setManagers] = React.useState<ApiResponseData<IHostManager>>();
    const [selectedManager, setSelectedManager] = React.useState<IHostManager | null>(null);

    React.useEffect(fetchInformation, []);

    return (<>
        <div className={styles["section"]}>
            <div className={styles["sec-line"]} />
            <h1 className={styles["sec-title"]}>About <em>{website.host.name}</em></h1>
            <p className={styles["sec-sub"]}>Our story, our team, and how to reach us</p>

            {/* ORG HERO */}
            <div className={styles["about-hero"]}>
                <div className={styles["about-logo-wrap"]}>
                    <img className={styles["about-logo-img"]} src={website.host.logo_url} alt={website.host.name} />
                </div>
                <div>
                    <div className={styles["about-detail-title"]}>{nameFirstWord}<br /><em>{nameRemain.join(" ")}</em></div>
                    <p className={styles["about-desc"]} dangerouslySetInnerHTML={{ __html: Utils.markdownToHtml(website.about_settings.info) }} />
                    <div className={styles["about-social-row"]}>
                        {Object.entries(website.social_accounts ?? {}).filter(([_, u]) => !!u).map(([social, username]) => (
                            <ReactRouterDom.NavLink key={social} target="_blank" to={Utils.getSocialLink(social, username)} className={Classname.build(styles["social-pill"], styles["unlinked"])} style={{ color: "var(--muted)" }}><i className={Utils.getSocialIcon(social)} />{username}</ReactRouterDom.NavLink>
                        ))}
                    </div>
                </div>
            </div>

            {/* CONTACT */}
            <div style={{ marginBottom: "3rem" }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]} style={{ fontSize: "1.8rem", marginBottom: "1.5rem" }}>Contact <em>Details</em></h2>
                <div className={styles["contact-grid"]}>
                    {(website.about_settings.show_physical_address && !!website.physical_addresses?.length && !BoolHelper.equalsAll("", website.physical_addresses.map((s) => s.city ?? s.country ?? s.state ?? s.street ?? ""))) && (<div className={styles["contact-card"]}>
                        <div className={styles["contact-card-icon"]}>📍</div>
                        <div className={styles["contact-card-lbl"]}>Physical Address</div>
                        {website.physical_addresses.map((entry, i) => (<div key={entry.country + i} className={styles["contact-card-val"]}>{entry.postal_code ? entry.postal_code + " " : ""}{entry.street ? entry.street + ", " : ""}{entry.city ? entry.city + " " : ""}{entry.state ? entry.state + ", " : ""}{entry.country ? i18nManager.queryCountryCurrency(entry.country, "country_code", "country_name") + " " : ""}</div>))}
                    </div>)}
                    {(website.about_settings.show_contact_emails_and_phones && !!website.emails_and_phones?.length && !BoolHelper.equalsAll("", website.emails_and_phones.map((s) => s.email ?? ""))) && (<div className={styles["contact-card"]}>
                        <div className={styles["contact-card-icon"]}>✉️</div>
                        <div className={styles["contact-card-lbl"]}>Email Addresses</div>
                        <div className={styles["contact-card-val"]}>
                            {website.emails_and_phones.map((entry, i) => (<a key={entry.email + i} href={`mailto:${entry.email}`}>{entry.email}</a>))}
                        </div>
                    </div>)}
                    {(website.about_settings.show_contact_emails_and_phones && !!website.emails_and_phones?.length && !BoolHelper.equalsAll("", website.emails_and_phones.map((s) => s.phone ?? ""))) && (<div className={styles["contact-card"]}>
                        <div className={styles["contact-card-icon"]}>📞</div>
                        <div className={styles["contact-card-lbl"]}>Phone Numbers</div>
                        <div className={styles["contact-card-val"]}>
                            {website.emails_and_phones.map((entry, i) => (<a key={entry.phone + i} href={`mailto:${entry.phone}`}>{entry.phone}</a>))}
                        </div>
                    </div>)}
                    {/* <div className={styles["contact-card"]}>
                        <div className={styles["contact-card-icon"]}>🕐</div>
                        <div className={styles["contact-card-lbl"]}>Opening Hours</div>
                        <div className={styles["contact-card-val"]}>
                            {ORG.openingHours.map(h => (
                                <div key={h.day} style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.2rem", fontSize: "0.83rem" }}>
                                    <span style={{ color: "var(--muted)" }}>{h.day}</span>
                                    <span style={{ marginLeft: "1rem", fontWeight: 600, color: h.time === "Closed" ? "var(--red)" : "var(--text)" }}>{h.time}</span>
                                </div>
                            ))}
                        </div>
                    </div> */}
                </div>
            </div>

            {/* SPONSORS */}
            {(website.about_settings.show_sponsors && !!website.sponsors?.length) && (<div style={{ marginBottom: "3rem" }}>
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]} style={{ fontSize: "1.8rem", marginBottom: "1.5rem" }}>Our <em>Sponsors</em></h2>
                <div className={styles["sponsors-list"]}>
                    {website.sponsors.map((s) => (
                        <div key={s} className={styles["sponsor-chip"]}>
                            <img alt={s} src={s} />
                        </div>
                    ))}
                </div>
            </div>)}

            {/* TEAM */}
            <div id="managers">
                <div className={styles["sec-line"]} />
                <h2 className={styles["sec-title"]} style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>Our <em>Team</em></h2>
                <p className={styles["sec-sub"]}>The people who make it all happen — click any card to view full profile</p>
                <div className={styles["staff-grid"]}>
                    {managers?.content?.map((m) => (
                        <div key={m.external_id} className={styles["staff-card"]} onClick={() => setSelectedManager(m)}>
                            <img className={styles["staff-img"]} src={m.half_body_picture_url} alt={m.display_name} loading="lazy" />
                            <div className={styles["staff-body"]}>
                                <span className={styles["staff-role-badge"]}>{m.display_role ?? m.role}</span>
                                <div className={styles["staff-name"]}>{m.display_name}</div>
                                {(!!m.settings.country) && (<div className={styles["staff-country"]}>{m.settings.country}</div>)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {selectedManager && <StaffModal member={selectedManager} onClose={() => setSelectedManager(null)} />}
    </>);

    function fetchInformation() {
        setManagers({});
        omnehostService.queryHostManagers(props.website.host.external_id, {
            size: 100,
            sort: "role,DESC",
        }).then(({ apiResponse }: SanitizedResponse<IHostManager>) => {
            setManagers(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

}

export default About;